/**
 * The WebSocket client.
 *
 * One socket for the whole application, multiplexed by topic — which is
 * what the backend's `/ws` endpoint is built for. Five sockets would mean
 * five reconnect strategies and five staleness clocks, and the fifth one
 * would be the one that silently died.
 *
 * Design properties, in the order they matter:
 *
 * **Staleness is a safety feature, not a nicety.** The server heartbeats
 * every 15s on an idle connection. A client that has not heard one is
 * entitled — required — to grey out every live price. A frozen number
 * rendered as live is a money-losing bug. `ConnectionState.stale` is what
 * the UI keys that off.
 *
 * **Reference-counted topics.** Two components watching BTCUSDT 1m produce
 * one subscription; the last one to unmount unsubscribes. Without this, a
 * route change leaves an exchange stream burning rate limit forever.
 *
 * **Subscriptions survive reconnects.** The socket re-subscribes to
 * everything on open. A component that subscribed before the first connect
 * completed still gets its data.
 *
 * **`dropped` is surfaced, not swallowed.** A non-zero counter in the
 * heartbeat means this browser is not keeping up and has lost the *oldest*
 * messages. That is the correct failure mode for market data, but the user
 * should be able to find out.
 */

import { websocketUrl } from './client';
import { fromDemo, toDemo } from '@/lib/demoNames';
import type {
  Timeframe,
  WsAlertEvent,
  WsCandleEvent,
  WsDeploymentsEvent,
  WsFrame,
  WsJobEvent,
  WsTopic,
} from './types';
import { WS_TOPIC_ALERTS, WS_TOPIC_DEPLOYMENTS, WS_TOPIC_JOBS, candleTopic } from './types';

export type ConnectionStatus = 'connecting' | 'open' | 'closed' | 'reconnecting';

export interface ConnectionState {
  status: ConnectionStatus;
  /**
   * No frame — event *or* heartbeat — for longer than the stale threshold.
   * When true, every live number on screen must be visibly degraded.
   */
  stale: boolean;
  /** Epoch ms of the last frame of any kind. 0 before the first. */
  lastMessageAt: number;
  /** Server-reported count of messages this client was too slow to read. */
  dropped: number;
  /** Consecutive failed connection attempts. Resets on a successful open. */
  attempts: number;
  /** Topics the server has confirmed. */
  activeTopics: string[];
  /** Last error text the server or the transport produced. */
  error: string | null;
}

type StateListener = (state: ConnectionState) => void;
type TopicListener = (data: unknown, topic: string) => void;

/** Server heartbeats every 15s; 3 missed beats plus slack is dead. */
const STALE_AFTER_MS = 50_000;
const STALE_TICK_MS = 2_000;
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 15_000;

function jitter(ms: number): number {
  // Full jitter. Without it, a backend restart makes every open tab retry
  // in lockstep, which is a self-inflicted thundering herd of one user.
  return Math.random() * ms;
}

class BheemaSocket {
  private socket: WebSocket | null = null;
  private readonly topicListeners = new Map<string, Set<TopicListener>>();
  private readonly refCounts = new Map<string, number>();
  private readonly stateListeners = new Set<StateListener>();
  private reconnectTimer: number | null = null;
  private staleTimer: number | null = null;
  private closedByUs = false;
  /** Invalidates callbacks from a socket replaced by a manual reconnect. */
  private generation = 0;

  private state: ConnectionState = {
    status: 'closed',
    stale: false,
    lastMessageAt: 0,
    dropped: 0,
    attempts: 0,
    activeTopics: [],
    error: null,
  };

  // ── State plumbing ──────────────────────────────────────────────────

  getState = (): ConnectionState => this.state;

  subscribeState = (listener: StateListener): (() => void) => {
    this.stateListeners.add(listener);
    return () => {
      this.stateListeners.delete(listener);
    };
  };

  private setState(patch: Partial<ConnectionState>): void {
    let changed = false;
    for (const key of Object.keys(patch) as Array<keyof ConnectionState>) {
      if (!Object.is(this.state[key], patch[key])) {
        changed = true;
        break;
      }
    }
    if (!changed) return;
    // A NEW object every time, because `useSyncExternalStore` compares by
    // identity — but only when something actually changed, because
    // returning a fresh object unconditionally is an infinite render loop.
    this.state = { ...this.state, ...patch };
    for (const listener of this.stateListeners) listener(this.state);
  }

  // ── Connection ──────────────────────────────────────────────────────

  private ensureConnected(): void {
    if (this.socket !== null) {
      const ready = this.socket.readyState;
      if (ready === WebSocket.OPEN || ready === WebSocket.CONNECTING) return;
    }
    this.closedByUs = false;
    this.setState({
      status: this.state.attempts > 0 ? 'reconnecting' : 'connecting',
      error: null,
    });

    let socket: WebSocket;
    const generation = ++this.generation;
    try {
      socket = new WebSocket(websocketUrl());
    } catch (error) {
      this.setState({ error: error instanceof Error ? error.message : String(error) });
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      if (generation !== this.generation || this.socket !== socket) return;
      this.setState({
        status: 'open',
        attempts: 0,
        error: null,
        lastMessageAt: Date.now(),
        stale: false,
      });
      const topics = [...this.refCounts.keys()];
      if (topics.length > 0) this.send({ action: 'subscribe', topics });
      this.startStaleClock();
    };

    socket.onmessage = (event: MessageEvent<string>) => {
      if (generation !== this.generation || this.socket !== socket) return;
      this.setState({ lastMessageAt: Date.now(), stale: false });
      let frame: WsFrame;
      try {
        frame = JSON.parse(event.data) as WsFrame;
      } catch {
        return;
      }
      this.handleFrame(toDemo(frame));
    };

    socket.onerror = () => {
      if (generation !== this.generation || this.socket !== socket) return;
      // `onerror` carries no useful detail in browsers; `onclose` follows.
      this.setState({ error: 'websocket transport error' });
    };

    socket.onclose = (event: CloseEvent) => {
      if (generation !== this.generation || this.socket !== socket) return;
      this.socket = null;
      this.stopStaleClock();
      if (this.closedByUs) {
        this.setState({ status: 'closed', stale: false, activeTopics: [] });
        return;
      }
      this.setState({
        status: 'reconnecting',
        stale: true,
        activeTopics: [],
        error: event.reason || `connection closed (${event.code})`,
      });
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    if (this.refCounts.size === 0 && this.stateListeners.size === 0) return;

    const attempts = this.state.attempts + 1;
    this.setState({ attempts });
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** (attempts - 1), RECONNECT_MAX_MS);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.ensureConnected();
    }, delay + jitter(delay));
  }

  private startStaleClock(): void {
    this.stopStaleClock();
    this.staleTimer = window.setInterval(() => {
      const silentFor = Date.now() - this.state.lastMessageAt;
      const stale = this.state.lastMessageAt > 0 && silentFor > STALE_AFTER_MS;
      if (stale !== this.state.stale) this.setState({ stale });
    }, STALE_TICK_MS);
  }

  private stopStaleClock(): void {
    if (this.staleTimer !== null) {
      window.clearInterval(this.staleTimer);
      this.staleTimer = null;
    }
  }

  private send(payload: Record<string, unknown>): void {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(fromDemo(payload)));
    }
  }

  private handleFrame(frame: WsFrame): void {
    switch (frame.type) {
      case 'welcome':
        this.setState({ activeTopics: frame.topics, error: frame.errors[0] ?? null });
        break;
      case 'subscribed':
      case 'unsubscribed':
        this.setState({
          activeTopics: frame.active,
          error: frame.errors.length > 0 ? (frame.errors[0] ?? null) : null,
        });
        break;
      case 'heartbeat':
        this.setState({ dropped: frame.dropped, activeTopics: frame.topics });
        break;
      case 'error':
        this.setState({ error: frame.message });
        break;
      case 'pong':
        break;
      case 'event': {
        const listeners = this.topicListeners.get(frame.topic);
        if (listeners) {
          for (const listener of listeners) listener(frame.data, frame.topic);
        }
        break;
      }
      default:
        break;
    }
  }

  // ── Topics ──────────────────────────────────────────────────────────

  /**
   * Listen to one topic. Returns an unsubscribe function.
   *
   * The socket connects lazily on the first subscription and the topic is
   * reference-counted, so mounting the same chart twice does not open two
   * exchange streams and unmounting one does not kill the other.
   */
  subscribe = (topic: string, listener: TopicListener): (() => void) => {
    let listeners = this.topicListeners.get(topic);
    if (!listeners) {
      listeners = new Set();
      this.topicListeners.set(topic, listeners);
    }
    listeners.add(listener);

    const count = (this.refCounts.get(topic) ?? 0) + 1;
    this.refCounts.set(topic, count);
    if (count === 1) this.send({ action: 'subscribe', topics: [topic] });

    this.ensureConnected();

    let released = false;
    return () => {
      if (released) return;
      released = true;
      const set = this.topicListeners.get(topic);
      set?.delete(listener);
      const remaining = (this.refCounts.get(topic) ?? 1) - 1;
      if (remaining <= 0) {
        this.refCounts.delete(topic);
        this.topicListeners.delete(topic);
        this.send({ action: 'unsubscribe', topics: [topic] });
      } else {
        this.refCounts.set(topic, remaining);
      }
    };
  };

  /** Round-trip liveness probe. The heartbeat usually makes this redundant. */
  ping = (): void => {
    this.send({ action: 'ping' });
  };

  /** Force a reconnect now — for a "reconnect" button next to a stale badge. */
  reconnect = (): void => {
    this.closedByUs = false;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      const replaced = this.socket;
      this.socket = null;
      ++this.generation;
      replaced.close();
    }
    this.stopStaleClock();
    this.setState({ attempts: 0 });
    this.ensureConnected();
  };

  /** Close and stay closed. Only for teardown. */
  close = (): void => {
    this.closedByUs = true;
    ++this.generation;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.stopStaleClock();
    this.socket?.close();
    this.socket = null;
    this.setState({ status: 'closed', stale: false, activeTopics: [] });
  };
}

/** The one socket. */
export const ws = new BheemaSocket();

// ══════════════════════════════════════════════════════════════════════
//  Typed subscription helpers
//
//  Pages use these rather than raw topic strings, so a typo in a timeframe
//  is a compile error instead of a silently empty stream.
// ══════════════════════════════════════════════════════════════════════

export function subscribeCandles(
  symbol: string,
  timeframe: Timeframe,
  listener: (candle: WsCandleEvent) => void,
): () => void {
  return ws.subscribe(candleTopic(symbol, timeframe), (data) =>
    listener(data as WsCandleEvent),
  );
}

export function subscribeAlerts(listener: (alert: WsAlertEvent) => void): () => void {
  return ws.subscribe(WS_TOPIC_ALERTS, (data) => listener(data as WsAlertEvent));
}

export function subscribeDeployments(
  listener: (event: WsDeploymentsEvent) => void,
): () => void {
  return ws.subscribe(WS_TOPIC_DEPLOYMENTS, (data) =>
    listener(data as WsDeploymentsEvent),
  );
}

export function subscribeJobs(listener: (job: WsJobEvent) => void): () => void {
  return ws.subscribe(WS_TOPIC_JOBS, (data) => listener(data as WsJobEvent));
}

export type { WsTopic };
